// Suppress ProseMirror RangeErrors, TransformErrors and IndexSizeErrors that
// surface from stale editor state. The editor self-recovers on the next user
// interaction, so swallowing these specific errors is preferable to crashing the whole view.

// TransformError extends Error (not RangeError), and matching on `name`
// avoids depending on prosemirror-transform's class identity.
// IndexSizeError is the DOMException the browser throws when the view syncs a
// stale DOM selection (Range.setEnd past the end of a text node).
const SUPPRESSIBLE_ERROR_NAMES = ['TransformError', 'IndexSizeError'];

const isSuppressibleError = (error) => error instanceof RangeError || SUPPRESSIBLE_ERROR_NAMES.includes(error?.name);

const wrap = (fn) => (...args) => {
	try {
		return fn(...args);
	} catch (error) {
		if (!isSuppressibleError(error)) {throw error;}
	}
};

// `dispatch` covers errors thrown while applying a transaction;
// `domObserver.flush` covers errors thrown while constructing one
// from queued DOM mutations (readDOMChange -> mkTr -> resolveSelection),
// which never reach `dispatch`.
export const wrapEditorViewForRangeErrors = (view) => {
	if (!view) {return;}

	view.dispatch = wrap(view.dispatch.bind(view));

	const observer = view.domObserver;

	if (observer && typeof observer.flush === 'function') {
		observer.flush = wrap(observer.flush.bind(observer));
	}
};

// Drains pending DOM mutations against the current document. Call before
// replacing content to prevent the observer from later resolving a stale
// selection against the new doc and throwing "Position -X out of range".
export const safeForceFlush = wrap((editor) => editor?.view?.domObserver?.forceFlush());
